import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Get the backend URL for creating short media URLs
const getBackendUrl = () => {
  return process.env.REACT_APP_BACKEND_URL || "";
};

// CSV Export - No character limit, includes full data URLs
export const exportDiamondsToCSV = (diamonds) => {
  if (!diamonds || diamonds.length === 0) {
    return;
  }

  // Find max number of images and videos across all diamonds
  const maxImages = Math.max(
    ...diamonds.map((d) => (d.images || []).length),
    0,
  );
  const maxVideos = Math.max(
    ...diamonds.map((d) => (d.videos || []).length),
    0,
  );

  // Build header row
  const headers = [
    "S.No",
    "Stock ID",
    "Shape",
    "Size (Carat)",
    "Color",
    "Clarity",
    "Cut",
    "Polish",
    "Symmetry",
    "Certificate",
    "Lab",
    "Measurement",
    "Fluorescence",
    "Location",
    "Ratio",
    "Shade",
    "Rap Discount (%)",
    "Price per Carat ($)",
    "Total Amount ($)",
    "No BGM",
    "Eye Clean",
    "Comment",
  ];

  // Add image and video headers
  for (let i = 0; i < maxImages; i++) {
    headers.push(`Image ${i + 1} Link`);
  }
  for (let i = 0; i < maxVideos; i++) {
    headers.push(`Video ${i + 1} Link`);
  }

  // Helper function to escape CSV fields
  const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    // If contains comma, newline, or quote, wrap in quotes and escape internal quotes
    if (str.includes(",") || str.includes("\n") || str.includes('"')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  // Build data rows
  const rows = diamonds.map((diamond, index) => {
    const row = [
      index + 1,
      diamond.stock_id || "",
      diamond.shape || "",
      diamond.size || "",
      diamond.colour || "",
      diamond.clarity || "",
      diamond.cut || "",
      diamond.polish || "",
      diamond.symmetry || "",
      diamond.certificate || "",
      diamond.lab || "",
      diamond.measurement || "",
      diamond.fluorescence || "",
      diamond.location || "",
      diamond.ratio || "",
      diamond.shade || "",
      diamond.rap_discount || "",
      diamond.price_per_carat || "",
      diamond.total_amount || "",
      diamond.no_bgm ? "Yes" : "No",
      diamond.eye_clean ? "Yes" : "No",
      diamond.comment || "",
    ];

    // Add ALL image links (full data URLs)
    for (let i = 0; i < maxImages; i++) {
      row.push(diamond.images && diamond.images[i] ? diamond.images[i] : "");
    }

    // Add ALL video links (full data URLs)
    for (let i = 0; i < maxVideos; i++) {
      row.push(diamond.videos && diamond.videos[i] ? diamond.videos[i] : "");
    }

    return row.map(escapeCSV).join(",");
  });

  // Combine headers and rows
  const csvContent = [headers.map(escapeCSV).join(","), ...rows].join("\n");

  // Create and download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `JustInnDiam_Selected_Diamonds_${timestamp}.csv`;
  saveAs(blob, filename);
};

// Excel Export - Uses short API URLs instead of base64 data
export const exportDiamondsToExcel = (diamonds) => {
  if (!diamonds || diamonds.length === 0) {
    return;
  }
  var IMGURL = "https://justinndiam.com:8000";

  const backendUrl = getBackendUrl();

  // Find max number of images and videos across all diamonds

  const maxImages = Math.max(
    ...diamonds.map((d) => (d.images || []).length),
    0,
  );

  const maxVideos = Math.max(
    ...diamonds.map((d) => (d.videos || []).length),
    0,
  );

  // Prepare data for Excel
  const excelData = diamonds.map((diamond, index) => {
    const row = {
      "S.No": index + 1,
      "Stock ID": diamond.stock_id || "",
      Shape: diamond.shape || "",
      "Size (Carat)": diamond.size || "",
      Color: diamond.colour || "",
      Clarity: diamond.clarity || "",
      Cut: diamond.cut || "",
      Polish: diamond.polish || "",
      Symmetry: diamond.symmetry || "",
      Certificate: diamond.certificate || "",
      Lab: diamond.lab || "",
      Measurement: diamond.measurement || "",
      Fluorescence: diamond.fluorescence || "",
      Location: diamond.location || "",
      Ratio: diamond.ratio || "",
      Shade: diamond.shade || "",
      "Rap Discount (%)": diamond.rap_discount || "",
      "Price per Carat ($)": diamond.price_per_carat || "",
      "Total Amount ($)": diamond.total_amount || "",
      "No BGM": diamond.no_bgm ? "Yes" : "No",
      "Eye Clean": diamond.eye_clean ? "Yes" : "No",
      Comment:
        diamond.comment && diamond.comment.length > 32000
          ? diamond.comment.substring(0, 32000) + "..."
          : diamond.comment || "",
    };

    // Add image links - use short API URLs
    for (let i = 0; i < maxImages; i++) {
      if (diamond.images && diamond.images[i]) {
        // Create a short URL that links to the image API endpoint
        row[`Image ${i + 1} Link`] =
          `${IMGURL}${diamond.images[i]}`;
      } else {
        row[`Image ${i + 1} Link`] = "";
      }
    }

    // Add video links - use short API URLs
    for (let i = 0; i < maxVideos; i++) {
      if (diamond.videos && diamond.videos[i]) {
        // Create a short URL that links to the video API endpoint
        row[`Video ${i + 1} Link`] =
          `${IMGURL}${diamond.videos[i]}`;
      } else {
        row[`Video ${i + 1} Link`] = "";
      }
    }

    return row;
  });

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Set column widths
  const columnWidths = [
    { wch: 8 }, // S.No
    { wch: 15 }, // Stock ID
    { wch: 12 }, // Shape
    { wch: 12 }, // Size
    { wch: 10 }, // Color
    { wch: 10 }, // Clarity
    { wch: 12 }, // Cut
    { wch: 12 }, // Polish
    { wch: 12 }, // Symmetry
    { wch: 15 }, // Certificate
    { wch: 10 }, // Lab
    { wch: 15 }, // Measurement
    { wch: 15 }, // Fluorescence
    { wch: 12 }, // Location
    { wch: 10 }, // Ratio
    { wch: 12 }, // Shade
    { wch: 15 }, // Rap Discount
    { wch: 18 }, // Price per Carat
    { wch: 18 }, // Total Amount
    { wch: 10 }, // No BGM
    { wch: 12 }, // Eye Clean
    { wch: 30 }, // Comment
  ];

  // Add widths for image and video columns
  for (let i = 0; i < maxImages; i++) {
    columnWidths.push({ wch: 70 });
  }
  for (let i = 0; i < maxVideos; i++) {
    columnWidths.push({ wch: 70 });
  }

  worksheet["!cols"] = columnWidths;

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Diamonds");

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `JustInnDiam_Selected_Diamonds_${timestamp}.xlsx`;

  // Save file
  saveAs(data, filename);
};
